import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, FileText, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';

export default function ClientDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({
    activeSubscription: null,
    pendingQuotes: 0,
    totalInvoices: 0
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchDashboardData = useCallback(async () => {
    try {
      const [subsRes, quotesRes, invoicesRes] = await Promise.all([
        fetch(`${API_URL}/api/payments/my-subscriptions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/payments/my-quotes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/payments/my-invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const subs = await subsRes.json();
      const quotes = await quotesRes.json();
      const invoices = await invoicesRes.json();

      setStats({
        activeSubscription: subs.find(s => s.status === 'active'),
        pendingQuotes: quotes.filter(q => q.status === 'pending').length,
        totalInvoices: invoices.length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const StatCard = ({ icon: Icon, title, value, description, color, href }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {href && (
          <Link to={href} className="mt-4 inline-flex items-center text-sm text-primary hover:underline">
            Voir les détails <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">
          Bonjour{user?.billing_info?.first_name ? `, ${user.billing_info.first_name}` : ''} !
        </h1>
        <p className="text-muted-foreground">Bienvenue dans votre espace client Axeris Labs</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        <StatCard
          icon={CreditCard}
          title="Abonnement"
          value={stats.activeSubscription ? 'Actif' : 'Aucun'}
          description={stats.activeSubscription ? `Expiré le ${new Date(stats.activeSubscription.current_period_end).toLocaleDateString('fr-FR')}` : 'Hébergement mensuel 15€'}
          color={stats.activeSubscription ? 'bg-green-100 text-green-600 dark:bg-green-900/30' : 'bg-muted text-muted-foreground'}
          href="/client/subscriptions"
        />
        <StatCard
          icon={Clock}
          title="Devis en attente"
          value={stats.pendingQuotes}
          description="À payer"
          color="bg-orange-100 text-orange-600 dark:bg-orange-900/30"
          href="/client/quotes"
        />
        <StatCard
          icon={FileText}
          title="Factures"
          value={stats.totalInvoices}
          description="Total"
          color="bg-primary/10 text-primary"
          href="/client/invoices"
        />
      </div>

      {/* Billing Info Alert */}
      {!user?.billing_info?.first_name && (
        <Card className="border-orange-500/50 bg-orange-50 dark:bg-orange-900/10">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-orange-800 dark:text-orange-200">Complétez vos informations</h3>
                <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                  Pour générer vos factures, veuillez compléter vos informations de facturation.
                </p>
                <Link to="/client/settings">
                  <Button size="sm" className="mt-3 bg-orange-600 hover:bg-orange-700 text-white">
                    Compléter mon profil
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
